// src/components/HomeView.js
import React, { useState } from 'react';
import styles from './HomeView.module.css';
import CharacterDisplay from './CharacterDisplay';
import InventoryDisplay from './InventoryDisplay';
// Import the background image
import landingBackgroundImage from '../assets/img/backgrounds/landingbackground.jpeg';

const HomeView = ({ 
  playerData, 
  setPlayerData, 
  onEquipItem, 
  onUnequipItem, 
  onUseItem, 
  onToggleItemLock, 
  isItemLocked, 
  tooltipsEnabled = true,
  onTabChange,
  worldState,
  onRest,
  canRest,
  onLocationChange
}) => {
  const [isCharacterPanelOpen, setIsCharacterPanelOpen] = useState(false);

  // Top row icons - 3 buttons
  const topRowIcons = [
    {
      id: 'shop',
      icon: '/img/buildingItems/shopFront.png',
      alt: 'Shop',
      title: 'Go to Shop - Browse items, upgrades, and buildings',
      onClick: () => onLocationChange && onLocationChange('shop'),
      type: 'location'
    },
    {
      id: 'rest',
      icon: '/img/icons/restIcon.png',
      alt: 'Rest',
      title: canRest ? "Rest and end the day" : "Cannot rest now",
      onClick: onRest,
      disabled: !canRest,
      type: 'action'
    },
    {
      id: 'wilderness',
      icon: '/img/icons/wildIcon.png',
      alt: 'Wilderness',
      title: 'Go to Wilderness - Gather resources, fight monsters, and explore',
      onClick: () => onLocationChange && onLocationChange('wilderness'),
      type: 'location'
    }
  ];

  // All buildings for easier management
  const allBuildings = {
    furnace: { 
      id: 'furnace', 
      icon: '/img/buildingItems/furnace.png', 
      alt: 'Furnace', 
      title: 'Furnace - Smelting & Charcoal'
    },
    forge: { 
      id: 'forge', 
      icon: '/img/buildingItems/forge.png', 
      alt: 'Forge', 
      title: 'Forge - Advanced Crafting'
    },
    campfire: { 
      id: 'campfire', 
      icon: '/img/icons/campfire_icon.png', 
      alt: 'Campfire', 
      title: 'Campfire - Cooking Food'
    },
    brewingStation: { 
      id: 'brewingStation', 
      icon: '/img/icons/brewing_icon.png', 
      alt: 'Brewing Station', 
      title: 'Brewing Station - Craft Potions'
    },
    workbench: { 
      id: 'workbench', 
      icon: '/img/buildingItems/workbench.png', 
      alt: 'Workbench', 
      title: 'Workbench - Basic Crafting'
    },
    tanningStation: { 
      id: 'tanningStation', 
      icon: '/img/icons/tanner_icon.png', 
      alt: 'Tanning Station', 
      title: 'Tanning Station - Leather Armor'
    },
    sawmill: { 
      id: 'sawmill', 
      icon: '/img/buildingItems/sawmill.png', 
      alt: 'Sawmill', 
      title: 'Sawmill - Wood Processing'
    },
    anvil: { 
      id: 'anvil', 
      icon: '/img/buildingItems/anvil.png', 
      alt: 'Anvil', 
      title: 'Anvil - Repair & Upgrades'
    },
    runecraftingStation: { 
      id: 'runecraftingStation', 
      icon: '/img/icons/runecraft_icon.png', 
      alt: 'Runecrafting Station', 
      title: 'Runecrafting Station - Magic Runes'
    },
    landPlot: { 
      id: 'landPlot', 
      icon: '/img/icons/land_icon.png', 
      alt: 'Land Plot', 
      title: 'Land Plot - Farming & Crops'
    },
    fletchingStation: { 
      id: 'fletchingStation', 
      icon: '/img/icons/fletching_icon.png', 
      alt: 'Fletching Station', 
      title: 'Fletching Station - Bows & Arrows'
    },
    thievingDummy: { 
      id: 'thievingDummy', 
      icon: '/img/icons/stealing_icon.png', 
      alt: 'Thieving Dummy', 
      title: 'Thieving Dummy - Stealth Training'
    }
  };

  // Management icons
  const managementIcons = {
    achievements: {
      id: 'achievements',
      icon: '/img/icons/achievementIcon.png',
      alt: 'Achievements',
      title: 'Achievements & Progress',
      onClick: () => onTabChange('achievements'),
      type: 'management'
    },
    skills: {
      id: 'skills',
      icon: '/img/icons/skillsIcon.png',
      alt: 'Skills',
      title: 'Skills & Progression',
      onClick: () => onTabChange('skills'),
      type: 'management'
    },
    quests: {
      id: 'quests',
      icon: '/img/buildingItems/questBoard.png',
      alt: 'Quests',
      title: 'Active Quests & Orders',
      onClick: () => onTabChange('quests'),
      type: 'management'
    }
  };

  // Mobile layout - 6 rows of 3 items each
  const mobileGridLayout = [
    // Row 2: Furnace, Achievements, Forge
    [allBuildings.furnace, managementIcons.achievements, allBuildings.forge],
    // Row 3: Anvil, skills, campfire
    [allBuildings.anvil, managementIcons.skills, allBuildings.campfire],
    // Row 4: Farming, Questboard, Tanner
    [allBuildings.landPlot, managementIcons.quests, allBuildings.tanningStation],
    // Row 5: Workbench, sawmill, fletching
    [allBuildings.workbench, allBuildings.sawmill, allBuildings.fletchingStation],
    // Row 6: Brew Station, Runecrafting, Thieving
    [allBuildings.brewingStation, allBuildings.runecraftingStation, allBuildings.thievingDummy]
  ];

  // Desktop layout (unchanged)
  const leftBuildingIcons = [
    allBuildings.furnace,
    allBuildings.forge,
    allBuildings.campfire,
    allBuildings.brewingStation,
    allBuildings.workbench,
    allBuildings.tanningStation
  ];

  // Right side - 3x3 grid arranged row by row for desktop
  const rightGridIcons = [
    // First row: Achievement + 2 buildings
    managementIcons.achievements,
    allBuildings.sawmill,
    allBuildings.anvil,
    
    // Second row: Skills + 2 buildings
    managementIcons.skills,
    allBuildings.runecraftingStation,
    allBuildings.landPlot,
    
    // Third row: Quests + 2 buildings
    managementIcons.quests,
    allBuildings.fletchingStation,
    allBuildings.thievingDummy
  ];

  const formatGameTime = (currentTimeInMinutes, wakeUpHour) => {
    const totalHours = Math.floor(currentTimeInMinutes / 60);
    const minutes = currentTimeInMinutes % 60;
    
    let displayHour = (wakeUpHour + totalHours) % 24;
    if (displayHour === 0) displayHour = 12;
    else if (displayHour > 12) displayHour -= 12;
    
    const period = (wakeUpHour + totalHours) % 24 < 12 ? 'AM' : 'PM';
    const minutesStr = minutes.toString().padStart(2, '0');
    
    return `${displayHour}:${minutesStr} ${period}`;
  };

  const handleBuildingClick = (buildingId) => {
    const isOwned = playerData?.buildings?.[buildingId]?.owned || false;
    if (isOwned) {
      onTabChange(`building-${buildingId}`);
    }
  };

  const renderIcon = (icon) => {
    if (icon.type === 'management') {
      return (
        <button
          key={icon.id}
          className={`${styles.iconButton} ${styles[icon.type]}`}
          onClick={icon.onClick}
          title={icon.title}
        >
          <img src={icon.icon} alt={icon.alt} className={styles.iconImage} />
        </button>
      );
    } else {
      const isOwned = playerData?.buildings?.[icon.id]?.owned || false;
      return (
        <div
          key={icon.id}
          className={`${styles.buildingIcon} ${!isOwned ? styles.disabledBuilding : styles.ownedBuilding}`}
          onClick={() => handleBuildingClick(icon.id)}
          title={icon.title}
        >
          <img 
            src={icon.icon} 
            alt={icon.alt} 
            className={styles.buildingImage}
          />
          {!isOwned && <div className={styles.lockOverlay}>🔒</div>}
        </div>
      );
    }
  };

  const toggleCharacterPanel = () => {
    setIsCharacterPanelOpen(!isCharacterPanelOpen);
  };

  return (
    <div className={styles.homeContainer}>
      {/* Background Image */}
      <div 
        className={styles.backgroundImage}
        style={{ backgroundImage: `url(${landingBackgroundImage})` }}
      />
      
      {/* Central Content Area */}
      <div className={styles.centralArea}>
        {/* Clock at top center */}
        <div className={styles.clockDisplay}>
          <img src="/img/icons/timeIcon.png" alt="Time" className={styles.clockIcon} />
          <span className={styles.timeText}>
            {formatGameTime(worldState?.currentTimeInMinutes || 0, 4)}
          </span>
        </div>

        {/* Main grid layout */}
        <div className={styles.iconGrid}>
          {/* Top row - 3 buttons */}
          <div className={styles.topRow}>
            {topRowIcons.map(icon => (
              <button
                key={icon.id}
                className={`${styles.iconButton} ${styles[icon.type]} ${icon.disabled ? styles.disabled : ''}`}
                onClick={icon.onClick}
                disabled={icon.disabled}
                title={icon.title}
              >
                <img src={icon.icon} alt={icon.alt} className={styles.iconImage} />
              </button>
            ))}
          </div>

          {/* Mobile layout - Single grid with 6 rows */}
          <div className={styles.mobileGridLayout}>
            {mobileGridLayout.map((row, rowIndex) => (
              <div key={rowIndex} className={styles.mobileRow}>
                {row.map(icon => renderIcon(icon))}
              </div>
            ))}
          </div>

          {/* Desktop layout - Left and Right sections */}
          <div className={styles.desktopLayout}>
            <div className={styles.mainContentGrid}>
              {/* Left side - 2x3 building grid */}
              <div className={styles.leftBuildingGrid}>
                {leftBuildingIcons.map(building => {
                  const isOwned = playerData?.buildings?.[building.id]?.owned || false;
                  return (
                    <div
                      key={building.id}
                      className={`${styles.buildingIcon} ${!isOwned ? styles.disabledBuilding : styles.ownedBuilding}`}
                      onClick={() => handleBuildingClick(building.id)}
                      title={building.title}
                    >
                      <img 
                        src={building.icon} 
                        alt={building.alt} 
                        className={styles.buildingImage}
                      />
                      {!isOwned && <div className={styles.lockOverlay}>🔒</div>}
                    </div>
                  );
                })}
              </div>

              {/* Right side - grid with buildings and management */}
              <div className={styles.rightGrid}>
                {rightGridIcons.map(icon => {
                  if (icon.type === 'management') {
                    return (
                      <button
                        key={icon.id}
                        className={`${styles.iconButton} ${styles[icon.type]}`}
                        onClick={icon.onClick}
                        title={icon.title}
                      >
                        <img src={icon.icon} alt={icon.alt} className={styles.iconImage} />
                      </button>
                    );
                  } else {
                    const isOwned = playerData?.buildings?.[icon.id]?.owned || false;
                    return (
                      <div
                        key={icon.id}
                        className={`${styles.buildingIcon} ${!isOwned ? styles.disabledBuilding : styles.ownedBuilding}`}
                        onClick={() => handleBuildingClick(icon.id)}
                        title={icon.title}
                      >
                        <img 
                          src={icon.icon} 
                          alt={icon.alt} 
                          className={styles.buildingImage}
                        />
                        {!isOwned && <div className={styles.lockOverlay}>🔒</div>}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side collapsible panel */}
      <div className={styles.rightPanelContainer}>
        {/* Single panel tab */}
        <div className={styles.panelTabs}>
          <button
            className={`${styles.panelTab} ${isCharacterPanelOpen ? styles.activeTab : ''}`}
            onClick={toggleCharacterPanel}
            title="Character & Inventory"
          >
            <img src="/img/icons/inventoryIcon.png" alt="Character & Inventory" className={styles.tabIcon} />
          </button>
        </div>

        {/* Collapsible panel content */}
        {isCharacterPanelOpen && (
          <div className={styles.collapsiblePanel}>
            <button 
              className={styles.closeButton}
              onClick={toggleCharacterPanel}
              title="Close Character & Inventory"
            >
              ✕
            </button>
            <div className={styles.panelContent}>
              <div className={styles.combinedContent}>
                <div className={styles.characterSection}>
                  <CharacterDisplay
                    playerData={playerData}
                    onUnequipItem={onUnequipItem}
                    tooltipsEnabled={tooltipsEnabled}
                  />
                </div>
                <div className={styles.inventorySection}>
                  <InventoryDisplay
                    playerData={playerData}
                    setPlayerData={setPlayerData}
                    onEquipItem={onEquipItem}
                    onUseItem={onUseItem}
                    onToggleItemLock={onToggleItemLock}
                    isItemLocked={isItemLocked}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeView; 